import { toImage } from '@/api/mappers/mapImage';
import { toApiPriceCreate, toApiPriceUpdate, toPrice } from '@/api/mappers/mapPrice';
import { toApiProduct, toApiProductCreate, toProduct } from '@/api/mappers/mapProduct';
import { toPromotion } from '@/api/mappers/mapPromotion';
import { toApiStatus, toStatus } from '@/api/mappers/mapStatus';
import { toSupplier } from '@/api/mappers/mapSupplier';
import {
  Dimensions as ApiDimensions,
  NetContent as ApiNetContent,
  NetContentUnitEnum,
} from '@/gen/api';
import { ProductSearchResponse as ApiProductSearchResponse } from '@/gen/api/models/ProductSearchResponse';
import { Sku as ApiSku } from '@/gen/api/models/Sku';
import { SkuCreateRequest as ApiSkuCreateRequest } from '@/gen/api/models/SkuCreateRequest';
import { SkuListResponse as ApiSkuListResponse } from '@/gen/api/models/SkuListResponse';
import { SkuUpdateRequest as ApiSkuUpdateRequest } from '@/gen/api/models/SkuUpdateRequest';
import { Dimensions } from '@/models/Dimensions';
import { NetContent } from '@/models/NetContent';
import {
  ProductSearchFacet,
  ProductSearchFacetCode,
  ProductSearchResult,
  SkuCreate,
  Sku as SkuModel,
  Skus,
  SkuUpdate,
} from '@/models/Sku';

export function toSku(apiSku: ApiSku): SkuModel {
  return {
    ...apiSku,
    externalCode: apiSku.externalCode ?? '',
    status: toStatus(apiSku.status),
    images: apiSku.images.map(toImage),
    product: apiSku.product && toProduct(apiSku.product),
    prices: apiSku.prices && apiSku.prices.map(toPrice),
    promotions: apiSku.promotions && apiSku.promotions.map(toPromotion),
    supplier: apiSku.supplier ? toSupplier(apiSku.supplier) : undefined,
  };
}

export function toSkus(response: ApiSkuListResponse): Skus {
  return {
    items: response.items.map(toSku),
    totalCount: response.totalCount,
    estimatedTotalCount: response.estimatedTotalCount,
  };
}

function isProductSearchFacetCode(value: string): value is ProductSearchFacetCode {
  return ['category', 'promotion', 'supplier'].includes(value);
}

function toProductSearchFacet(
  facet: ApiProductSearchResponse['facets'][number],
): ProductSearchFacet | null {
  if (!isProductSearchFacetCode(facet.code)) {
    return null;
  }

  return {
    code: facet.code,
    name: facet.name,
    values: facet.values,
  };
}

export function toProductSearchResult(response: ApiProductSearchResponse): ProductSearchResult {
  return {
    items: response.items.map(toSku),
    totalCount: response.totalCount,
    estimatedTotalCount: response.estimatedTotalCount,
    facets: response.facets
      .map(toProductSearchFacet)
      .filter((facet): facet is ProductSearchFacet => facet !== null),
  };
}

export function toApiSkuCreateRequest(sku: SkuCreate): ApiSkuCreateRequest {
  const product = sku.product
    ? {
        ...sku.product,
        name: sku.product.name || sku.name,
        description: sku.product.description || sku.description,
      }
    : undefined;

  return {
    ...sku,
    code: sku.code || sku.externalCode,
    moq: sku.moq ? parseInt(sku.moq.toString(), 10) : 0,
    quantity: sku.quantity ? parseInt(sku.quantity.toString(), 10) : 0,
    dimensions: sku.dimensions ? toApiDimensions(sku.dimensions) : undefined,
    netContent: sku.netContent ? toApiNetContent(sku.netContent) : undefined,
    status: sku.status ? toApiStatus(sku.status) : undefined,
    product: product ? toApiProductCreate(product) : undefined,
    price: sku.price ? toApiPriceCreate(sku.price) : undefined,
    images: undefined,
  };
}

function isEmpty(value: number | string): boolean {
  return value === null || value === undefined || value === 0 || value === '';
}

function toApiDimensions(dimensions: Dimensions): ApiDimensions | undefined {
  if (
    isEmpty(dimensions.length) ||
    isEmpty(dimensions.width) ||
    isEmpty(dimensions.height) ||
    isEmpty(dimensions.unit)
  ) {
    return;
  }

  return {
    length: dimensions.length,
    width: dimensions.width,
    height: dimensions.height,
    unit: dimensions.unit,
  };
}

export function toApiNetContent(netContent: NetContent): ApiNetContent | undefined {
  if (isEmpty(netContent.value) || isEmpty(netContent.unit)) {
    return;
  }

  if (!isNetContentUnitEnum(netContent.unit)) {
    console.warn(`Invalid net content unit: ${netContent.unit}`);
    return;
  }

  return {
    value: netContent.value,
    unit: netContent.unit,
  };
}

function isNetContentUnitEnum(value: string): value is NetContentUnitEnum {
  return Object.values(NetContentUnitEnum).includes(value as NetContentUnitEnum);
}

export function toApiSkuUpdateRequest(sku: SkuUpdate): ApiSkuUpdateRequest {
  return {
    ...sku,
    moq: sku.moq ? parseInt(sku.moq.toString(), 10) : 0,
    quantity: sku.quantity ? parseInt(sku.quantity.toString(), 10) : 0,
    dimensions: sku.dimensions ? toApiDimensions(sku.dimensions) : undefined,
    netContent: sku.netContent ? toApiNetContent(sku.netContent) : undefined,
    status: sku.status ? toApiStatus(sku.status) : undefined,
    product: sku.product ? toApiProduct(sku.product) : undefined,
    price: sku.price ? toApiPriceUpdate(sku.price) : undefined,
    images: undefined,
  };
}
